<template>
  <div class="productCard text-tdefault-color">
    <div class="card p-4 bg-white rounded-lg">
      <div class="grid grid-rows">
        <div class="w-full">
          <div class="grid grid-cols-12">
            <div class="content-start lg:content-center justify-items-center col-span-3 lg:col-span-1">
              <img
                :src="product.imageUrl"
                @error="handleImageError"
                class="max-w-20 lg:max-w-32 max-h-20 lg:max-h-32 align-middle"
                :alt="product.productTitle ? `Image of ${$toTitleCase(product.productTitle)}` : 'Product image'"
              />
            </div>
            <div class="col-span-9 lg:col-span-11">
              <div class="grid grid-rows md:grid-cols-5">
                <div class="grid grid-rows ml-6 lg:col-span-4">
                  <div class="mb-2">
                    <div v-if="relationalProduct">
                      <div class="bg-green-100 mb-2 p-2 rounded-lg inline-block text-green-800">Vervangend product</div>
                    </div>
                    <div
                      v-if="product.materialStatusCode == 30"
                      class="bg-green-100 mb-2 p-2 rounded-lg inline-block text-green-800"
                    >
                      Op bestelling verkrijgbaar
                    </div>
                    <div v-else-if="!available" class="bg-red-200 mb-2 p-2 rounded-lg inline-block text-red-900">
                      Niet beschikbaar
                    </div>
                    <div class="cursor-pointer">
                      <h3 @click.prevent="goToProductDetailPage()">{{ $toTitleCase(product.productTitle) }}</h3>
                    </div>
                  </div>
                  <div class="grid grid-rows" v-if="!relationalProduct">
                    <div class="inline-block mb-2">
                      <span class="mr-2">
                        Artikelcode <b>{{ product.productCode }}</b>
                      </span>
                      <span class="mr-2">
                        Bestelnummer <b>{{ product.productId }}</b>
                      </span>
                      <span class="mr-2">
                        Verpakking <b>{{ product.packagingUnit }} {{ product.packagingLabel }}</b>
                      </span>
                    </div>
                    <div class="flex flex-col mb-2">
                      <div v-if="!assortmentFeatureEnabled">
                        <div>
                          <span
                            class="inline-flex items-center gap-1 border-b border-dashed"
                            :class="
                              product.isReembursed
                                ? 'text-success-default border-success-default'
                                : 'text-danger-text border-danger-text'
                            "
                          >
                            <component
                              :is="product.isReembursed ? 'CheckCircleIcon' : 'ExclamationCircleIconOutline'"
                              class="h-5 w-5"
                            />
                            {{ product.isReembursed ? 'Vergoed' : 'Niet vergoed' }}
                          </span>
                        </div>
                      </div>
                      <div v-if="requireSerialNumber" class="w-1/3 mt-2">
                        <v-input-group-simple-2
                          id="serialNumber"
                          v-model:value="product.serialNumber"
                          maxlength="128"
                          :rules="{
                            required: true
                          }"
                          label="Serienummer"
                          :inputid="product.productId"
                          placeholder="Voer het serienummer van het aparaat in."
                          type="text"
                          @input="updateSerialNumber($event)"
                        />
                      </div>
                    </div>
                  </div>
                  <div class="flex mb-2" v-if="showTimeEstimation() && product.materialStatusCode != 30">
                    <InformationCircleIcon
                      v-if="showInfoIcon()"
                      :title="atpDetails.hoverText"
                      class="h-6 w-6 flex-shrink-0 mr-1 text-tdefault-color cursor-pointer inline-block fill-orange-500"
                      aria-hidden="true"
                    />
                    <TruckIcon
                      v-else
                      :class="getATPCheckStatusColorClass()"
                      :title="atpDetails.details"
                      class="h-6 w-6 flex-shrink-0 mr-1 text-tdefault-color cursor-pointer inline-block"
                      aria-hidden="true"
                    />
                    {{ atpDetails.details }}
                  </div>
                  <div class="grid mb-2" v-if="!relationalProduct">
                    <span class="mt-2" v-if="product.instruction && !assortmentFeatureEnabled">
                      <v-checkbox-group
                        class="font-bold mb-1"
                        v-model="product.instructionInput"
                        :inputid="'Instructie-' + product.productId"
                        :initvalue="product.instructionInput"
                        :overwrite-value="product.instructionInput"
                        @update:model-value="updateShoppingCartItem()"
                        :disabled="product.givenToPatient"
                        :class="{ disabledBlock: product.givenToPatient }"
                      >
                        <span>Instructie bij levering gewenst</span>
                      </v-checkbox-group>
                    </span>
                    <span class="mt-2" v-if="displayGivenToPatient">
                      <v-checkbox-group
                        class="font-bold mb-1"
                        v-model="product.givenToPatient"
                        :inputid="'levering-' + product.productId"
                        :initvalue="product.givenToPatient"
                        :overwrite-value="product.givenToPatient"
                        @update:model-value="updateShoppingCartItem()"
                      >
                        <span class="mr-2">Meegegeven aan patiënt</span>
                      </v-checkbox-group>
                    </span>
                    <span class="mt-2" v-if="displayFillUpStock">
                      <v-checkbox-group
                        class="font-bold mb-1"
                        label="Magazijn bijvullen"
                        v-model="product.fillUpStock"
                        :inputid="'magazijn-' + product.productId"
                        :initvalue="product.fillUpStock"
                        :overwrite-value="product.fillUpStock"
                        @update:model-value="updateShoppingCartItem()"
                      >
                      </v-checkbox-group>
                      <span
                        class="mr-2"
                        v-if="product.givenToPatient && product.fillUpStock && warehouses && warehouses.length > 1"
                      >
                        <label
                          :id="`${product.warehouseCode}-label`"
                          :for="`qty-${product.warehouseCode}`"
                          class="sr-only"
                        >
                          Kies magazijn
                        </label>
                        <select
                          :aria-labelledby="product.warehouseCode ? `${product.warehouseCode}-label` : undefined"
                          :aria-label="!product.warehouseCode ? ariaLabel || 'Kies magazijn' : undefined"
                          class="rounded text-center border border-bdark p-2 mb-3 w-40 mt-2"
                          ref="warehouses"
                          name="warehouses"
                          data-vv-as="Magazijn"
                          :rule="{ required: true }"
                          v-model="product.warehouseCode"
                        >
                          <option :value="undefined" disabled="disabled" selected="selected">Kies magazijn</option>
                          <option v-for="warehouse in warehouses" :key="warehouse.Id" :value="warehouse.Id">
                            {{ warehouse.Description }}
                          </option>
                        </select>
                      </span>
                    </span>
                  </div>
                </div>
                <div class="w-full justify-items-start lg:justify-items-end self-center ml-6 lg:ml-0 mt-2 lg:mt-0">
                  <div class="" v-if="relationalProduct">
                    <button
                      @click.prevent="addRelationalProduct"
                      class="flex justify-center py-2 px-2 md:px-4 shadow-sm text-sm font-medium text-white bg-cta rounded-full hover:bg-primary-default/50 items-center mr-2"
                    >
                      <ArrowsRightLeftIcon
                        class="h-3 md:h-5 lg:h-5 w-3 md:w-5 lg:w-5 inline-block mr-2"
                        aria-hidden="true"
                      />
                      <span class="">Product vervangen</span>
                    </button>
                  </div>
                  <div class="" v-else>
                    Aantal
                    <label :id="`${product.productId}-label`" :for="`qty-${product.productId}`" class="sr-only">
                      Aantal
                    </label>
                    <select
                      :aria-labelledby="product.productId ? `${product.productId}-label` : undefined"
                      :aria-label="!product.productId ? ariaLabel || 'Aantal' : undefined"
                      class="quantity rounded text-center mx-2 border border-bdark py-1 w-14"
                      v-model="product.quantity"
                      @change="changeProductQuantiy"
                      :initselected="product.quantity"
                      :disabled="!available"
                    >
                      <option
                        v-for="(quantity, index) in productQuantities"
                        :key="index"
                        :data-index="index"
                        :value="quantity"
                      >
                        {{ quantity }}
                      </option>
                      <option value="more">meer...</option>
                    </select>
                    <span @click="removeShoppingCartItem()">
                      <TrashIcon
                        class="h-6 w-6 flex-shrink-0 mr-2 text-tdefault-color cursor-pointer inline-block"
                        aria-hidden="true"
                      />
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <!-- Modals section -->
    <generic-modal :has-close-button="true" v-if="showProductQuantityInput" @close="closeModal">
      <template #body>
        <h4>Vul hieronder het te bestellen aantal in en klik op OK</h4>
        <input
          class="text-center w-12 py-1 border border-b-dark outline-none rounded my-2"
          type="text"
          v-model="dialogQuantity"
          maxlength="3"
          autocomplete="off"
        />
        <div class="text-sm py-4" v-if="showMinAlert">
          <ExclamationCircleIconOutline
            title="Verzekerd"
            class="h-6 w-6 flex-shrink-0 mr-1 text-tdefault-color cursor-pointer inline-block"
            aria-hidden="true"
          />
          Bestelhoeveelheid per {{ product.minOrderQty }}
        </div>
      </template>
      <template #footer>
        <v-button tagelement="button" md primary @click.prevent="saveProductQuantity(dialogQuantity)">OK</v-button>
      </template>
    </generic-modal>
    <generic-modal v-if="deleteProductOverlay">
      <template #header> Let op! </template>
      <template #body>
        <p>{{ removeProductPopupMessage }}</p>
      </template>
      <template #footer>
        <v-button tagelement="button" hollow transparent md @click.prevent="closeModal">Annuleren</v-button>
        <v-button md primary @click.prevent="handleRemoveShoppingCartItem()">Bevestigen</v-button>
      </template>
    </generic-modal>
  </div>
</template>

<script>
import router from '@/router';
import VCheckboxGroup from '@/components/shared/form/CheckboxGroup';
import GenericModal from '@/components/shared/GenericModal';
import { assortmentFeatureEnabled$ } from '@/services';
import { computed, onBeforeUnmount, ref, watch } from 'vue';
import { useStore } from 'vuex';
import debounce from 'lodash/debounce';
import {
  CheckIcon,
  ExclamationCircleIcon,
  TruckIcon,
  InformationCircleIcon,
  ArrowsRightLeftIcon
} from '@heroicons/vue/20/solid';
import {
  ExclamationCircleIcon as ExclamationCircleIconOutline,
  TrashIcon,
  CheckCircleIcon
} from '@heroicons/vue/24/outline';

const ATP_DEBOUNCE_MS = 400;

export default {
  name: 'ProductCard',
  components: {
    VCheckboxGroup,
    GenericModal,
    CheckIcon,
    ExclamationCircleIcon,
    TruckIcon,
    InformationCircleIcon,
    TrashIcon,
    ExclamationCircleIconOutline,
    CheckCircleIcon,
    ArrowsRightLeftIcon
  },
  props: {
    product: {
      type: Object,
      required: true
    },
    relationalProduct: {
      type: Boolean,
      default: false
    },
    atpDetails: {
      type: Object
    },
    available: {
      type: Boolean,
      default: true
    }
  },
  emits: ['addRelationalProduct', 'update', 'remove', 'atp'],
  setup(props, { emit }) {
    const store = useStore();
    const assortmentFeatureEnabled = ref(null);
    const showMinAlert = ref(false);
    const showProductQuantityInput = ref(false);
    const lastNumericQuantity = ref(props.product.quantity);
    const dialogQuantity = ref(0);
    const serialNumberErrorMessage = ref('* Dit veld is verplicht.');
    const deleteProductOverlay = ref(false);
    const siteCode = ref(store.state.navigation.siteCode);
    const isCTypeMaterialAvailable = ref(store.state.navigation.cTypeMaterials);

    const removeProductPopupMessage = computed(() => store.getters['cart/getPopupMessage']);
    const warehouses = computed(() => store.getters['navigation/getWarehouses']);
    const hasWarehouses = computed(() => {
      return warehouses.value && warehouses.value.length > 0;
    });
    const productQuantities = computed(() => {
      let quantities = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10];
      if (quantities.indexOf(props.product.quantity) < 0) {
        quantities.push(props.product.quantity);
      }
      return quantities;
    });
    const autoFillUpStockWhenGivenToPatient = computed(() => {
      return props.product.supportAutoFillUpStockWhenGivenToPatient;
    });
    const requireSerialNumber = computed(() => {
      return props.product.supportSerialNumber && props.product.givenToPatient;
    });
    const displayGivenToPatient = computed(() => {
      // E40-7654 : Hide GivenToPatient for Depot Checkout.
      // GivenToPatient = TRUE, FillUpStock = TRUE
      if (router.currentRoute.value.name == 'aanvullenmagazijnCheckout') {
        return false;
      }
      // E40-7654 : (If C type consignment material recevied from SAP) Hide GivenToPatient for Catalog Checkout - Non Consignment Product.
      // GivenToPatient = FALSE, FillUpStock = FALSE
      if (
        (isCTypeMaterialAvailable.value && props.product.isConsignmentProduct == false) ||
        props.product.isNutritionProduct
      ) {
        return false;
      }
      return props.product.supportGivenToPatient;
    });
    const displayFillUpStock = computed(() => {
      return (
        props.product.supportFillUpStock && props.product.givenToPatient && !autoFillUpStockWhenGivenToPatient.value
      );
    });

    assortmentFeatureEnabled$.subscribe((value) => (assortmentFeatureEnabled.value = value));

    const updateProductFlags = () => {
      if (isCTypeMaterialAvailable.value && props.product.isConsignmentProduct === false) {
        props.product.fillUpStock = false;
        props.product.givenToPatient = false;
      }
    };
    const addRelationalProduct = () => {
      emit('addRelationalProduct');
    };
    const closeModal = () => {
      showProductQuantityInput.value = false;
      deleteProductOverlay.value = false;
    };
    const getATPCheckStatusColorClass = () => {
      // Don't show color for products with MaterialStatusCode = 30
      if (String(props.product.MaterialStatusCode) === '30') {
        return '';
      }
      if (props.atpDetails.ATPCheckColorCode >= 1 && props.atpDetails.ATPCheckColorCode <= 2) {
        return 'fill-green-500';
      }
      if (props.atpDetails.ATPCheckColorCode >= 3 && props.atpDetails.ATPCheckColorCode <= 6) {
        return 'fill-orange-500';
      }
      if (props.atpDetails.ATPCheckColorCode >= 7) {
        return 'fill-red-500';
      }
    };
    const showInfoIcon = () => {
      // Don't show info icon for products with MaterialStatusCode = 30
      if (String(props.product.MaterialStatusCode) === '30') {
        return false;
      }
      return props.atpDetails.ATPCheckColorCode && ['2', '4', '6'].includes(props.atpDetails.ATPCheckColorCode);
    };
    const goToProductDetailPage = () => {
      if (!assortmentFeatureEnabled.value) {
        router.push('/catalog/product/' + props.product.productId);
        return;
      }
      router.push('/aanvullenmagazijn/product/' + props.product.productId);
    };
    const restrictCount = (count) => {
      let regex = /^([0-9]+)$/;
      if (!regex.test(count) || !count || count <= 0) {
        props.product.quantity = props.product.minOrderQty;
        showMinAlert.value = true;
      }
      updateShoppingCartItem();
    };
    const updateShoppingCartItem = () => {
      emit('update', props.product);
      showProductQuantityInput.value = false;
    };
    const removeShoppingCartItem = () => {
      deleteProductOverlay.value = true;
    };
    const handleRemoveShoppingCartItem = () => {
      emit('remove', props.product);
    };
    const reduceProductQuantity = () => {
      if (props.product.quantity === props.product.minOrderQty) {
        showMinAlert.value = true;
        return;
      }
      props.product.quantity = Math.min(Number(props.product.quantity) - 1);
      updateShoppingCartItem();
    };
    const increaseProductQuantity = () => {
      props.product.quantity = Math.min(Number(props.product.quantity) + 1);
      updateShoppingCartItem();
    };
    const changeInstructionInput = (selected) => {
      props.product.instructionInput = selected;
    };
    const showTimeEstimation = () => {
      if (String(props.product.MaterialStatusCode) === '30') {
        return false;
      }
      return props.atpDetails && props.atpDetails.ATPCheckColorCode;
    };
    const saveProductQuantity = (inputValue) => {
      let regex = /^([0-9]+)$/;
      if (!regex.test(inputValue) || !inputValue || inputValue <= 0) {
        showMinAlert.value = true;
        return;
      }
      showMinAlert.value = false;
      props.product.quantity = dialogQuantity.value / 1;
      updateShoppingCartItem();
      showProductQuantityInput.value = false;
    };
    const changeProductQuantiy = (e) => {
      if (e.currentTarget.value === 'more') {
        dialogQuantity.value = lastNumericQuantity.value;
        props.product.quantity = lastNumericQuantity.value;
        showProductQuantityInput.value = true;
        return;
      }
      updateShoppingCartItem();
    };
    const updateSerialNumber = (event) => {
      props.product.serialNumber = event.target.value;
      if (props.product.serialNumber) {
        updateShoppingCartItem();
      }
    };

    const emitDebouncedAtpCheck = debounce((product) => {
      emit('atp', product);
    }, ATP_DEBOUNCE_MS);

    watch(
      () => props.product.quantity,
      (newVal, oldVal) => {
        if (!isNaN(newVal) && oldVal !== undefined && newVal != 0) {
          lastNumericQuantity.value = newVal / 1;
          emitDebouncedAtpCheck(props.product);
        }
      },
      { immediate: true }
    );
    onBeforeUnmount(() => {
      emitDebouncedAtpCheck.cancel();
    });
    watch(
      () => isCTypeMaterialAvailable,
      (newVal) => {
        updateProductFlags();
      },
      { immediate: true }
    );
    watch(
      () => props.product.isConsignmentProduct,
      (newVal) => {
        updateProductFlags();
      },
      { immediate: true }
    );

    return {
      removeProductPopupMessage,
      warehouses,
      assortmentFeatureEnabled,
      siteCode,
      isCTypeMaterialAvailable,
      showMinAlert,
      showProductQuantityInput,
      lastNumericQuantity,
      dialogQuantity,
      serialNumberErrorMessage,
      deleteProductOverlay,
      updateProductFlags,
      addRelationalProduct,
      closeModal,
      getATPCheckStatusColorClass,
      showInfoIcon,
      goToProductDetailPage,
      restrictCount,
      updateShoppingCartItem,
      removeShoppingCartItem,
      handleRemoveShoppingCartItem,
      reduceProductQuantity,
      increaseProductQuantity,
      changeInstructionInput,
      showTimeEstimation,
      saveProductQuantity,
      changeProductQuantiy,
      updateSerialNumber,
      hasWarehouses,
      productQuantities,
      autoFillUpStockWhenGivenToPatient,
      requireSerialNumber,
      displayGivenToPatient,
      displayFillUpStock
    };
  }
};
</script>
